'use client';
import React from 'react';
import { createRoot } from 'react-dom/client';
import Modal from '../components/Modal';
import { useToggle } from '@react-hookz/web';
import useTour from '../hooks/useTour';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Tooltip from 'modules/common/components/Tooltip';
import { slackUrl, whatsappDorothee, whatsappMartin } from 'modules/App/config';
// import { setTimeout } from 'timers';
// import { useSubscription } from 'modules/subscription';

const ActiveTourContainer: React.FC<any> = ({ children }) => {
  const { tour, goToNextStep, stopTour } = useTour();
  const router = useRouter();
  const { pathname } = router;
  const [, toggleRerender] = useToggle(true);

  const [modalOpened, toggleModal] = useToggle(false);

  const stop = () => {
    toggleModal(false);
    stopTour();
  };
  const next = React.useCallback(() => {
    toggleModal(false);
    goToNextStep();
  }, [toggleModal, goToNextStep]);

  const skipButton = (
    <button className="link text-white font-normal outline-none flex-1 text-left text-xs" onClick={stop}>
      Ne plus afficher le didacticiel
    </button>
  );

  const nextButton = (
    <button className="btn btn-xs btn-white" onClick={next}>
      Ok, super
    </button>
  );

  const footer = (
    <>
      {skipButton}
      {nextButton}
    </>
  );

  const steps = [
    {
      page: /^\/searched-profiles.*?$/,
      type: 'modal',
      footer: (
        <>
          {skipButton}
          <Link href="/searched-profiles" className="btn btn-white flex-1" onClick={next}>
            C’est parti
          </Link>
        </>
      ),
      description: (
        <>
          <p className="text-2xl font-bold">Bienvenue sur Headlinker</p>
          <p className="mt-5 font-normal text-sm">
            On te propose un didacticiel de 1 minute pour comprendre comment ça marche et tout ce qu’on propose
          </p>
        </>
      ),
    },
    {
      page: /^\/searched-profiles.*?$/,
      type: 'tooltip',
      selector: '#search-items',
      // terminate: /^\/events\/\[id\]\/edit$/,
      footer,
      description: (
        <p>
          Ici, tu peux filtrer pour découvrir les postes à pourvoir dans différents secteurs, métiers et zones géographiques. Nous les avons
          pré-remplies en utilisant ton profil.
          <small className="block">Si il n’y a pas de résultat, élargis tes filtres.</small>
        </p>
      ),
    },
    {
      page: /^\/searched-profiles.*?$/,
      type: 'tooltip',
      selector: '.table > thead > tr',
      footer,
      tooltipPlacement: 'topRight',
      // terminate: /^\/events\/\[id\]\/edit$/,
      description: (
        <p>
          Dans ce tableau, tu retrouves les postes à pourvoir avec
          <ul>
            <li>
              <strong>La prime:</strong> Ce que tu toucheras si tu proposes un candidat retenu
            </li>
            <li>
              <strong>Exclu:</strong> Si le recruteur a le client en exclusivité
            </li>
            <li>
              <strong>Le nombre de contacts en cours:</strong> ”Prêt à sourcer” si personne n’a encore proposé un candidat
            </li>
            <li>
              <strong>Le détail:</strong> Pour voir tous les détails de la mission et contacter le recruteur, clique sur <strong>Voir</strong>
            </li>
          </ul>
        </p>
      ),
    },
    {
      page: /^\/searched-profiles.*?$/,
      type: 'modal',
      // terminate: /^\/events\/\[id\]\/edit$/,
      footer: (
        <>
          {skipButton}
          <Link href="/available-profiles" className="btn btn-white flex-1" onClick={next}>
            On continue !
          </Link>
        </>
      ),
      description: (
        <div>
          <h2 className="mb-5">✨ Quelques conseils pour bien commencer</h2>
          <ul className="[&>li]:mb-2">
            <li>
              <strong>Contacte le recruteur avant toute action sur la mission</strong>
              <div>
                Les missions sont sensées être à jour et désactivées au bout de 30 jours d’inactivité mais on est jamais trop prudent.
                <small className="block">
                  Tu as son linkedIn et son téléphone dans sa fiche et vu que nous sommes entre personnes de confiance, tout va bien se passser 😀)
                </small>
              </div>
            </li>
            <li>
              <strong>Demande une exclusivité</strong> si tu penses être capable de trouver un candidat rapidement.
              <small className="block">Ça se fait par téléphone et l’autre recruteur pourra ensuite te désigner comme seul sourceur.</small>
            </li>
          </ul>
        </div>
      ),
    },
    {
      page: /^\/available-profiles.*?$/,
      type: 'tooltip',
      tooltipPlacement: 'bottomLeft',
      selector: '[href="/fr/available-profiles"]',
      // terminate: /^\/events\/\[id\]\/edit$/,
      footer: (
        <>
          {skipButton}
          <Link href="/members" className="btn btn-white btn-xs" onClick={next}>
            Compris !
          </Link>
        </>
      ),
      description: (
        <div>
          Ici, c’est le même principe mais on liste les <strong>candidats disponibles</strong> pour prendre une mission rapidement, avec la prime que
          le recruteur souhaite recevoir si tel est le cas.
        </div>
      ),
    },
    {
      page: /^\/members$/,
      type: 'tooltip',
      tooltipPlacement: 'bottomLeft',
      selector: '[href="/fr/members"]',
      // terminate: /^\/events\/\[id\]\/edit$/,
      footer,
      description: (
        <div>
          Ici, tu vas retrouver tous les membres de la communauté
          <small className="block">
            Tu peux donc trouver les recruteurs dans ton secteur ou métier ou intervenant dans la même zone que toi. Idéal pour trouver des synergies.
          </small>
          <p className="mt-5">
            <strong>✨ Petite astuce:</strong> Tu peux voir leur activité sur Headlinker dans le tableau, ainsi que leur taux de réussite.
          </p>
        </div>
      ),
    },
    {
      page: /^\/members$/,
      type: 'tooltip',
      selector: '#notification-center',
      footer,
      description: <>Ici, tu peux voir les notifications de nouvelles missions ou candidats qui sont susceptibles de t’intéresser.</>,
    },
    {
      page: /^\/members$/,
      type: 'tooltip',
      selector: '#user-menu',
      footer: (
        <>
          {skipButton}
          <Link href="/calendar" className="btn btn-white btn-xs" onClick={next}>
            Excellent
          </Link>
        </>
      ),
      description: (
        <>
          Ici, tu peux configurer ton compte
          <ul>
            <li>
              <strong>Profil:</strong> Modifie tes informations personnelles
            </li>
            <li>
              <strong>Notifications:</strong> Choisis quoi recevoir, quand et par quel moyen (email ou notification dans le navigateur)
              <small className="mt-0.5 block border border-white border-dashed rounded-md p-1">
                ✨ C’est une partie très importante, on y reviendra à la fin de ce tutorial
              </small>
            </li>
            <li>
              <strong>Intégrations:</strong> Utilise notre extension ou connecte toi avec Wink
            </li>
          </ul>
        </>
      ),
    },
    {
      page: /^\/calendar$/,
      type: 'tooltip',
      selector: '.rbc-row-content-scroll-container',
      haloSelector: '[href="/fr/calendar"]',
      footer,
      description: (
        <>
          <p className="mb-5">Ici, c’est le calendrier des événements. On y retrouve:</p>
          <ul>
            <li>
              <strong>Les sessions ”Cold Call”:</strong> Des sessions de 1h pour arrêter de procrastiner et aller chercher de nouveaux clients{' '}
              <small className="block">C’est inclus et gratuit pour ton premier mois chez Headlinker avec la formule Booster</small>
            </li>
            <li>
              <strong>Les apéros:</strong> Virtuels ou en présentiel, pour discuter de sourcerie et de recrutement
            </li>
            <li>
              <strong>Les masterclass:</strong> Pour passer au niveau supérieur
            </li>
            <li>
              et beaucoup d’autres <strong>ateliers</strong>
            </li>
          </ul>
        </>
      ),
    },
    {
      page: /^\/calendar$/,
      type: 'modal',
      footer: (
        <>
          {skipButton}
          <Link href="/my-invoices" className="btn btn-white" onClick={next}>
            Excellent
          </Link>
        </>
      ),
      description: (
        <div>
          <h2 className="mb-5">✨ Nos conseils</h2>
          <ul className="[&>li]:mb-2">
            <li>
              <strong>Regarde régulièrement le calendrier</strong>
              <small className="block">Notre but est de proposer toujours plus d’évènements pour te faire progresser</small>
            </li>
            <li>
              <strong>Inscris toi aux évènements</strong>
              <small className="block">
                Certains des évènements ont un nombre de places limité, inscris toi dès que tu le peux en cliquant sur l’évènement. En plus, tu
                recevras un rappel 1h avant 😉.
              </small>
            </li>
          </ul>
        </div>
      ),
    },
    {
      page: /^\/my-invoices$/,
      type: 'tooltip',
      selector: '[href="/fr/my-invoices"]',
      footer: (
        <>
          {skipButton}
          <Link href="/user/subscription" className="btn btn-white btn-xs" onClick={next}>
            Super !
          </Link>
        </>
      ),
      description: (
        <>
          Ici, Tu retrouves tes factures.
          <small className="block">Elles sont générées automatiquement et tu peux renseigner toi-même ton adresse</small>
        </>
      ),
    },
    {
      page: /^\/user\/subscription$/,
      type: 'tooltip',
      selector: '[href="/fr/user/subscription"]',
      footer,
      description: <>Et enfin, tu peux découvrir toutes nos formules pour passer au niveau supérieur</>,
    },
    {
      page: /^\/user\/subscription$/,
      type: 'modal',
      footer: (
        <>
          <Link href="/user/settings" className="btn btn-white" onClick={next}>
            Génial, c’est parti !
          </Link>
        </>
      ),
      description: (
        <div>
          <h2 className="mb-5">✨ Et voilà, tu as tout compris !</h2>
          <p className="mb-5">Tu peux maintenant commencer à explorer l'interface et n’oublie pas</p>
          <ul className="[&>li]:mb-2">
            <li>
              <strong>
                Inscris-toi au{' '}
                <a href={slackUrl} target="_blank" rel="noopener noreferrer" className="link">
                  Slack
                </a>
              </strong>
              <small className="block">C’est notre outil de communication privé et tu pourras y trouver des informations utiles</small>
            </li>
            <li>
              <strong>Tu as des questions ?</strong>
              <small className="block">
                Contacte{' '}
                <a href={whatsappMartin} className="link" target="_blank" rel="noopener noreferrer">
                  Martin
                </a>{' '}
                si tu rencontres un bug ou un problème technique et{' '}
                <a href={whatsappDorothee} className="link" target="_blank" rel="noopener noreferrer">
                  Dorothée
                </a>{' '}
                pour toute autre question
              </small>
            </li>
            <li>
              <strong>Configure tes notifications</strong>
              <small className="block">Pour être sûr de ne rien rater</small>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  const step = !tour?.done && tour?.lastStep !== undefined && steps[tour?.lastStep];

  React.useEffect(() => {
    if (!tour?.done && tour?.lastStep && !step) {
      stopTour();
    }
  }, [tour?.done, step, tour?.lastStep, stopTour]);

  React.useEffect(() => {
    if (!step) {
      return;
    }

    // if (step.terminate && step.terminate.test(pathname)) {
    //   goToNextStep();
    //   return;
    // }

    if (!step.page.test(pathname)) {
      return;
    }
    if (step.type === 'modal' && !modalOpened) {
      toggleModal(true);
    }
    if (step.type === 'tooltip' && step.selector) {
      const targetElement = document.querySelector(step.selector);
      const domNode = document.createElement('span');

      if (!targetElement) {
        setTimeout(toggleRerender, 300);
        // Until targetElement exists
        return;
      }
      const targetHaloElement = document.querySelector(step.haloSelector || step.selector);
      if (!targetHaloElement) {
        setTimeout(toggleRerender, 300);
        // Until targetHaloElement exists
        return;
      }
      if (!(targetHaloElement as any).style.position) {
        (targetHaloElement as any).style.position = 'relative';
      }
      targetHaloElement.appendChild(domNode);

      const root = createRoot(domNode);

      const arrowClasses: { [key: string]: string } = {
        br_tr:
          'w-0 h-0 absolute bottom-0 right-12 border-l-[20px] border-l-transparent border-t-[10px] border-t-primary border-r-[20px] border-r-transparent',
        tr_br:
          'w-0 h-0 absolute top-0 right-12 border-l-[20px] border-l-transparent border-b-[10px] border-b-primary border-r-[20px] border-r-transparent',
      };

      const tooltip = (
        <Tooltip
          placement={step.tooltipPlacement || 'topRight'}
          visible
          showArrow={false}
          destroyTooltipOnHide
          onPopupAlign={(_, { points }) => {
            const tooltipArrowClassName = document.querySelector('#tooltip-arrow')?.className;
            const arrowClass = points && arrowClasses[points?.join('_')];

            if (arrowClass && tooltipArrowClassName !== arrowClass && document.querySelector('#tooltip-arrow') !== null) {
              // @ts-ignore
              document.querySelector('#tooltip-arrow').className = arrowClass;
            }
          }}
          overlayInnerStyle={{ background: 'var(--colorPrimary)' }}
          overlayStyle={{ opacity: 1 }}
          overlay={
            <div className="flex flex-col rounded-xl p-3 gap-3 sm:p-5 sm:gap-5 max-w-[20rem] text-white">
              <div id="tooltip-arrow" className={arrowClasses[step.tooltipPlacement === 'topRight' ? 'tr_br' : 'br_tr']}></div>
              <div className="flex-1 text-sm sm:text-base text-left">{step.description}</div>
              {step.footer && <footer className="flex flex-wrap items-center justify-between">{step.footer}</footer>}
            </div>
          }
        >
          <span
            onClick={(e) => {
              next();
              const clickEvent = new MouseEvent('click', {
                bubbles: true, // Allow the event to bubble up the DOM tree
                cancelable: true, // Allow the event to be canceled
                view: window, // Specify the window as the view
              });
              if ((e.target as any)?.parentNode?.parentNode) {
                (e.target as any)?.parentNode?.parentNode.dispatchEvent(clickEvent);
              }
            }}
            className="absolute inset-0"
          ></span>
        </Tooltip>
      );
      const previousStyle = (targetHaloElement as any).style['box-shadow'];
      (targetHaloElement as any).style['box-shadow'] = 'var(--colorPrimary) 0px 0px 20px 3px';
      root.render(tooltip);
      console.log('step', step); //eslint-disable-line
      return () => {
        root.unmount();
        (targetHaloElement as any).style['box-shadow'] = previousStyle;
        const parentElement = domNode.parentNode; // Get the parent of the node you want to remove
        if (parentElement) {
          parentElement.removeChild(domNode); // Remove the domNode from its parent
        }
      };
    }
  }, [router, step, modalOpened, next, pathname, toggleModal, toggleRerender, goToNextStep]);

  if (!step) {
    return null;
  }

  return (
    <>
      <Modal open={modalOpened} className="bg-primary" footer={step.footer}>
        {step.description}
      </Modal>
      {children}
    </>
  );
};

const TourContainer: React.FC<any> = ({ children }: any) => {
  const { tour } = useTour();

  if (tour?.done) {
    return children;
  }

  return <ActiveTourContainer>{children}</ActiveTourContainer>;
};

export default TourContainer;
